import { Component, OnInit, ChangeDetectionStrategy, inject, input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Constants } from '@app/consts';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment-timezone';
import {ToastrService} from 'ngx-toastr';
import {LaunchRegistrationPlatform} from '@app/modules/shared/models/project-registration-platform';
import { SbInputTimeComponent } from '../../../_components/unified/sb-input/sb-input-time.component';
import { CustomTimezonePickerComponent } from '../../../_components/custom-timezone-picker/custom-timezone-picker.component';
import { NgSelectComponent } from '@ng-select/ng-select';
//
// @TODO: add this in a separate file to reuse
interface ScheduleDate {
  schedule_date?: string;
  schedule_time?: string;
  timezone?: any;
  sync_influencers_before_launch?: boolean;
  require_successful_sync?: boolean;
  sync_hours_before_launch?: number;
  /** The slug of the install synced before the launch, for an older server. */
  sync_registration_platform?: string;
  /** The install synced before the launch (a project may install one platform more than once). */
  sync_project_registration_platform_id?: number;
  /** Sync once the campaign has launched, to push the share links it created. */
  sync_influencers_after_launch?: boolean;
  /** The install synced after the launch: one whose syncs push share links. */
  sync_after_launch_registration_platform_id?: number;
}

type ScheduleForm = FormGroup<{
  /** `MM/DD/YYYY` as loaded, or the `Date` the field writes once edited. */
  schedule_date: FormControl<string | Date | null>;
  schedule_time: FormControl<string | null>;
  timezone: FormControl<any>;
  sync_influencers_before_launch: FormControl<boolean | null>;
  require_successful_sync: FormControl<boolean | null>;
  sync_hours_before_launch: FormControl<number | null>;
  sync_project_registration_platform_id: FormControl<number | null>;
  sync_influencers_after_launch: FormControl<boolean | null>;
  sync_after_launch_registration_platform_id: FormControl<number | null>;
}>;

@Component({
  selector: 'app-launch-schedule-modal',
  templateUrl: './launch-schedule-modal.component.html',
  styleUrls: ['./launch-schedule-modal.component.scss'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, SbInputTimeComponent, CustomTimezonePickerComponent, NgSelectComponent]
})
export class LaunchScheduleModalComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  modal = inject(NgbActiveModal);
  constants = inject(Constants);
  private toastr = inject(ToastrService);

  public form: ScheduleForm;

  public min: Date = new Date();
  public max: Date = this.min;

  public readonly schedule = input<ScheduleDate>({});
  public readonly launchType = input('');
  public timezones: object[] = [];
  public readonly hasActiveRegistrationPlatforms = input(false);
  /** An active install pushes share links: the launch may sync after it. */
  public readonly hasPushRegistrationPlatforms = input(false);
  public readonly registrationPlatforms = input<LaunchRegistrationPlatform[]>([]);
  public maxSyncHours = 24;
  public syncHoursOptions: number[] = [];
  public activePlatforms: LaunchRegistrationPlatform[] = [];
  /** The installs a sync after the launch can run on: active, complete, pushing share links. */
  public pushPlatforms: LaunchRegistrationPlatform[] = [];
  public formattedSyncHoursOptions: any[] = [];

  constructor() {
    const constants = this.constants;

    //
    // Set timezone list
    this.timezones = constants.timezone;
  }

  ngOnInit() {
    this.activePlatforms = this.registrationPlatforms().filter(p => p.active);
    this.pushPlatforms = this.registrationPlatforms().filter(p => p.active && p.integration && p.links_active);
    //
    // Create form
    this.createForm();
    this.calculateMaxSyncHours();
  }

  timezoneSelect(event: any) {
    this.form.patchValue({timezone: event.value});
  }

  createForm() {
    let schedule_date: string | Date;
    let schedule_time: string;
    const schedule = this.schedule();
    if (schedule?.schedule_date) {
      schedule_date = moment(schedule?.schedule_date).format('MM/DD/YYYY');
      // A schedule saved for a day now past stays valid when the dialog opens again: the date
      // picker checks a loaded date against its minimum, which the old picker did not.
      if (moment(schedule_date, 'MM/DD/YYYY').isBefore(this.min, 'day')) {
        this.min = moment(schedule_date, 'MM/DD/YYYY').toDate();
      }
    }
    if (schedule?.schedule_time) {
      schedule_time = moment(schedule?.schedule_time, 'HH:mm:ss').format('h:mm A');
    }

    this.form = this.formBuilder.group({
      schedule_date: [schedule_date, Validators.required],
      schedule_time: [schedule_time, Validators.required],
      timezone: [schedule?.timezone, Validators.required],
      sync_influencers_before_launch: [schedule?.sync_influencers_before_launch !== undefined ? schedule.sync_influencers_before_launch : false],
      require_successful_sync: [schedule?.require_successful_sync !== undefined ? schedule.require_successful_sync : true],
      sync_hours_before_launch: [schedule?.sync_hours_before_launch || null],
      sync_project_registration_platform_id: [this.initialBeforeLaunchInstall()],
      sync_influencers_after_launch: [!!schedule?.sync_influencers_after_launch],
      sync_after_launch_registration_platform_id: [schedule?.sync_after_launch_registration_platform_id || null],
    });
    this.form.patchValue({timezone: schedule?.timezone});

    // Watch for date/time changes to recalculate max sync hours
    this.form.get('schedule_date')?.valueChanges.subscribe(() => this.calculateMaxSyncHours());
    this.form.get('schedule_time')?.valueChanges.subscribe(() => this.calculateMaxSyncHours());
    this.form.get('timezone')?.valueChanges.subscribe(() => this.calculateMaxSyncHours());
    this.form.get('sync_hours_before_launch')?.valueChanges.subscribe(() => this.updateSyncDateTime());

    this.form.get('sync_influencers_before_launch')?.valueChanges.subscribe(checked => {
      const platformControl = this.form.get('sync_project_registration_platform_id');
      const hoursControl = this.form.get('sync_hours_before_launch');

      if (checked && this.hasActiveRegistrationPlatforms()) {
        platformControl?.setValidators([Validators.required]);
        hoursControl?.setValidators([Validators.required]);
      } else {
        platformControl?.clearValidators();
        hoursControl?.clearValidators();
      }
      platformControl?.updateValueAndValidity();
      hoursControl?.updateValueAndValidity();
    });

    // The sync after the launch needs its install while the box is ticked
    this.form.get('sync_influencers_after_launch')?.valueChanges.subscribe(checked => {
      const platformControl = this.form.get('sync_after_launch_registration_platform_id');
      if (checked && this.hasPushRegistrationPlatforms()) {
        platformControl?.setValidators([Validators.required]);
      } else {
        platformControl?.clearValidators();
      }
      platformControl?.updateValueAndValidity();
    });

    // Clear validators if no active registration platforms
    if (!this.hasActiveRegistrationPlatforms()) {
      this.form.get('sync_project_registration_platform_id')?.clearValidators();
      this.form.get('sync_hours_before_launch')?.clearValidators();
      this.form.get('sync_project_registration_platform_id')?.updateValueAndValidity();
      this.form.get('sync_hours_before_launch')?.updateValueAndValidity();
    }

    this.form.get('sync_influencers_before_launch')?.updateValueAndValidity();
    this.form.get('sync_influencers_after_launch')?.updateValueAndValidity();
  }

  /**
   * The install an earlier schedule chose for the sync before the launch: by its id, or, for a
   * schedule that named only a platform slug, that platform's first active install.
   */
  initialBeforeLaunchInstall(): number | null {
    const schedule = this.schedule();
    if (schedule?.sync_project_registration_platform_id) {
      return schedule.sync_project_registration_platform_id;
    }
    const slug = schedule?.sync_registration_platform;
    return slug ? this.activePlatforms.find(p => p.slug === slug)?.id || null : null;
  }

  getDefaultSelectedPlatform(): number | null {
    // Select first active platform by default
    const activePlatform = this.activePlatforms.length > 0 ? this.activePlatforms[0] : null;
    return activePlatform ? activePlatform.id : null;
  }

  close() {
    this.modal.close();
  }

  save() {
    //
    // Validate form
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const selectedTz = this.form.get('timezone').value;
    const timezoneValue = selectedTz?.nameValue ? selectedTz.nameValue : selectedTz;

    this.form.patchValue({timezone: selectedTz});
    const sendData: any = {
      schedule_date: moment(this.form.get('schedule_date').value, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('YYYY-MM-DD'),
      schedule_time: moment.tz(this.form.get('schedule_time').value, 'h:mm A', timezoneValue).format('HH:mm:ss'),
      timezone: this.form.get('timezone').value?.nameValue ? this.form.get('timezone').value.nameValue : selectedTz
    };

    // Add sync options if enabled
    if (this.hasActiveRegistrationPlatforms()) {
      sendData.sync_influencers_before_launch = this.form.get('sync_influencers_before_launch').value;
      sendData.require_successful_sync = this.form.get('require_successful_sync').value;
      sendData.sync_hours_before_launch = this.form.get('sync_hours_before_launch').value;
      const install = this.activePlatforms.find(p => p.id === this.form.get('sync_project_registration_platform_id')?.value);
      sendData.sync_project_registration_platform_id = install?.id || null;
      // The install's platform slug, for an older server
      sendData.sync_registration_platform = install?.slug || '';
    }
    // The sync after the launch: on one install pushing share links
    if (this.hasPushRegistrationPlatforms()) {
      const afterLaunch = !!this.form.get('sync_influencers_after_launch').value;
      sendData.sync_influencers_after_launch = afterLaunch;
      sendData.sync_after_launch_registration_platform_id = afterLaunch
        ? this.form.get('sync_after_launch_registration_platform_id').value
        : null;
    }
    const selectedDateTime: any = moment.tz(sendData.schedule_date + ' ' + sendData.schedule_time, sendData.timezone).utc();
    const diff = selectedDateTime.utc().diff(moment().utc());
    /** If time not passed */
    if (diff <= 0) {
      // throw error
      this.toastr.error(`Current selected date and time is older than the current time`);
    } else {
      //
      // Close modal with current schedule object
      this.modal.close(sendData);
    }
  }

  getTimezoneValue(event) {
    this.form.patchValue({timezone: event});
  }

  /** The zone `save()` schedules in, so the fields' "today" and "now" are that zone's. */
  timezoneName(): string | null {
    const value = this.form.get('timezone').value;
    const name = value?.nameValue ?? value;
    return typeof name === 'string' && name ? name : null;
  }

  /** The page's own message for an empty date; the field shows its own for a half-typed or too early one. */
  dateError(): string {
    return this.requiredError('schedule_date') ? 'Date is required' : '';
  }

  timeError(): string {
    return this.requiredError('schedule_time') ? 'Time is required' : '';
  }

  /**
   * A time quick pick that lands on another day (In 24 hours late in the evening) moves the date
   * with it, as a calendar pick of that day would: the day at the current time of day.
   */
  onTimePickDay(day: string): void {
    const now = new Date();
    const date = moment(day, 'YYYY-MM-DD').toDate();
    date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
    this.form.get('schedule_date').setValue(date);
    this.form.get('schedule_date').markAsDirty();
  }

  private requiredError(name: 'schedule_date' | 'schedule_time'): boolean {
    const control = this.form.get(name);
    return !!control.errors?.['required'] && (control.touched || control.dirty);
  }

  calculateMaxSyncHours() {
    // Always show 1-12 hours as options
    this.maxSyncHours = 12;

    // Generate options array
    this.syncHoursOptions = [];
    for (let i = 1; i <= this.maxSyncHours; i++) {
      this.syncHoursOptions.push(i);
    }

    this.formattedSyncHoursOptions = this.syncHoursOptions.map(hour => ({
      value: hour,
      label: `${hour} hour${hour > 1 ? 's' : ''} before launch`
    }));

    // Calculate and display sync date/time
    this.updateSyncDateTime();
  }

  updateSyncDateTime() {
    const scheduleDate = this.form?.get('schedule_date')?.value;
    const scheduleTime = this.form?.get('schedule_time')?.value;
    const timezone = this.form?.get('timezone')?.value;
    const hoursBeforeLaunch = this.form?.get('sync_hours_before_launch')?.value || 12;

    if (scheduleDate && scheduleTime && timezone) {
      const tzValue = timezone?.nameValue || timezone;
      const launchDateTime = moment.tz(
        moment(scheduleDate).format('YYYY-MM-DD') + ' ' + moment(scheduleTime, 'h:mm A').format('HH:mm:ss'),
        tzValue
      );
      const syncDateTime = launchDateTime.clone().subtract(hoursBeforeLaunch, 'hours');
    }
  }
}
